/**
 * Frame244 – Order death certificates (IAD / death certificate workflow)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ClipboardList } from 'lucide-react';
import { Button } from '../ui/Button';
import { SettlementLayout } from '../settlement/SettlementLayout';
import DocumentUploadSection from '../settlement/DocumentUploadSection';
import { useSettlementFrameData } from '../../hooks/useSettlementFrameData';
import { useDeathCertDocuments } from '../../hooks/useDeathCertDocuments';
import {
  DEATH_CERT_DOC,
  DEATH_CERT_FRAME_DATA,
  deathCertRoute,
} from '../../utils/deathCertWorkflow';

type OrderSource = 'funeral' | 'vital' | 'other' | '';

export default function Frame244() {
  const navigate = useNavigate();
  const { estateId } = useParams<{ estateId?: string }>();

  const { data, save, loading: frameLoading } = useSettlementFrameData(estateId ?? null, DEATH_CERT_FRAME_DATA.orderForm);
  const { documents, onUpload, onDelete } = useDeathCertDocuments(estateId, 'death-cert-doc:244');

  const [orderSource, setOrderSource] = useState<OrderSource>('');
  const [orderedThrough, setOrderedThrough] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [copiesOrdered, setCopiesOrdered] = useState('');
  const [dateOrdered, setDateOrdered] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setOrderSource((data.orderSource as OrderSource) || '');
    setOrderedThrough(String(data.orderedThrough || ''));
    setContactPhone(String(data.contactPhone || ''));
    setCopiesOrdered(data.copiesOrdered != null ? String(data.copiesOrdered) : '');
    setDateOrdered(String(data.dateOrdered || ''));
    setExpectedDate(String(data.expectedDate || ''));
    setNotes(String(data.notes || ''));
  }, [data]);

  const persist = async () => {
    await save({
      orderSource,
      orderedThrough,
      contactPhone,
      copiesOrdered: copiesOrdered === '' ? '' : Number(copiesOrdered),
      dateOrdered,
      expectedDate,
      notes,
    });
  };

  const handleNext = async () => {
    await persist();
    navigate(deathCertRoute('/frame-21', estateId));
  };

  const handleBack = async () => {
    await persist();
    navigate(deathCertRoute('/frame-20-1', estateId));
  };

  return (
    <SettlementLayout currentPhase="iad" currentStep="death-certificates" estateId={estateId ?? null}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-start gap-3">
          <ClipboardList className="w-8 h-8 text-cyan-700 shrink-0 mt-1" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Death Certificates</h1>
            <p className="text-gray-600">
              Record how and when you ordered certified copies. Upload receipts or confirmations when you have them.
            </p>
          </div>
        </div>

        {!estateId && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Sign in to an estate and open this page with your estate ID in the URL to save progress and upload documents
            to the settlement vault.
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">How are you ordering?</label>
            <div className="flex flex-col sm:flex-row gap-3">
              {(
                [
                  { id: 'funeral' as const, label: 'Through the funeral home' },
                  { id: 'vital' as const, label: 'State / county vital records' },
                  { id: 'other' as const, label: 'Other channel' },
                ] as const
              ).map((opt) => (
                <label
                  key={opt.id}
                  className={`flex-1 border rounded-lg px-4 py-3 cursor-pointer text-sm ${
                    orderSource === opt.id ? 'border-cyan-600 bg-cyan-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    className="mr-2"
                    checked={orderSource === opt.id}
                    onChange={() => setOrderSource(opt.id)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-800 mb-1">Provider or office name</label>
              <input
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={orderedThrough}
                onChange={(e) => setOrderedThrough(e.target.value)}
                onBlur={() => void persist()}
                placeholder="e.g. Riverside Funeral Home or MD Vital Records"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Contact phone</label>
              <input
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                onBlur={() => void persist()}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Number of certified copies</label>
              <input
                type="number"
                min={0}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={copiesOrdered}
                onChange={(e) => setCopiesOrdered(e.target.value)}
                onBlur={() => void persist()}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Date ordered</label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={dateOrdered}
                onChange={(e) => setDateOrdered(e.target.value)}
                onBlur={() => void persist()}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Expected ready / ship date</label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={expectedDate}
                onChange={(e) => setExpectedDate(e.target.value)}
                onBlur={() => void persist()}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Notes</label>
            <textarea
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm min-h-[88px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onBlur={() => void persist()}
              placeholder="Reference numbers, fees paid, expedited shipping, etc."
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 shadow-sm">
          <DocumentUploadSection
            documents={documents}
            onUpload={async (file) => {
              await onUpload(file, DEATH_CERT_DOC.orderReceipt);
            }}
            onDelete={(id) => onDelete(id)}
            disabled={!estateId}
          />
          <p className="text-xs text-gray-500 mt-2">
            Upload order confirmations, email receipts, or scanned forms. Files are stored on the estate settlement record.
          </p>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => void handleBack()} disabled={frameLoading}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={() => void handleNext()} size="lg" disabled={frameLoading}>
            Track orders
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </SettlementLayout>
  );
}
